// ============================================================
// 系统管理 / 登录认证 / 仪表盘 API（独立于 admin.ts，复用其 http 实例）
// ============================================================
import http from '@/api/admin';

export interface PageResult<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
}

// ---- 认证 / 当前账号 ----

export interface RoleBrief {
  id: string;
  code: string;
  name: string;
}

export interface MenuNode {
  id: string;
  parentId: string | null;
  code: string;
  name: string;
  type: 'directory' | 'menu' | 'button';
  path: string | null;
  component: string | null;
  icon: string | null;
  permCode: string | null;
  sort: number;
  visible: boolean;
  /** 所属工作区：app=App 后台(默认) / mall=官网后台。 */
  workspace: string;
  children: MenuNode[];
}

export interface AdminProfile {
  id: string;
  username: string;
  name: string;
  email: string | null;
  status: string;
  lastLoginAt: string | null;
  superAdmin: boolean;
  roles: RoleBrief[];
  permissions: string[];
  menus: MenuNode[];
}

export interface LoginResponse {
  accessToken: string;
  tokenType: string;
  profile: AdminProfile;
}

export const authApi = {
  login(username: string, password: string): Promise<LoginResponse> {
    return http
      .post<LoginResponse>('/auth/login', { username, password })
      .then((res) => res.data);
  },
  me(): Promise<AdminProfile> {
    return http.get<AdminProfile>('/auth/me').then((res) => res.data);
  },
  changePassword(oldPassword: string, newPassword: string): Promise<void> {
    return http.post('/auth/change-password', { oldPassword, newPassword }).then(() => undefined);
  },
  logout(): Promise<void> {
    return http.post('/auth/logout').then(() => undefined);
  }
};

// ---- 账号管理 ----

export interface AccountItem {
  id: string;
  username: string;
  name: string;
  email: string | null;
  status: string;
  lastLoginAt: string | null;
  lastLoginIp: string | null;
  roleIds: string[];
  createdAt: string;
}

export interface CreateAccountBody {
  username: string;
  password: string;
  name: string;
  email?: string | null;
  status?: string;
  roleIds: string[];
}

export interface UpdateAccountBody {
  name: string;
  email?: string | null;
  status: string;
  roleIds: string[];
}

export const accountApi = {
  list(params: { q?: string; status?: string; page?: number; size?: number } = {}): Promise<
    PageResult<AccountItem>
  > {
    return http.get<PageResult<AccountItem>>('/accounts', { params }).then((res) => res.data);
  },
  create(body: CreateAccountBody): Promise<AccountItem> {
    return http.post<AccountItem>('/accounts', body).then((res) => res.data);
  },
  update(id: string, body: UpdateAccountBody): Promise<AccountItem> {
    return http.put<AccountItem>(`/accounts/${id}`, body).then((res) => res.data);
  },
  resetPassword(id: string, newPassword: string): Promise<void> {
    return http.post(`/accounts/${id}/reset-password`, { newPassword }).then(() => undefined);
  },
  remove(id: string): Promise<void> {
    return http.delete(`/accounts/${id}`).then(() => undefined);
  }
};

// ---- 角色管理 ----

export interface RoleItem {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  sort: number;
  createdAt: string;
}

export const roleApi = {
  list(): Promise<RoleItem[]> {
    return http.get<RoleItem[]>('/roles').then((res) => res.data);
  },
  menuIds(id: string): Promise<string[]> {
    return http.get<string[]>(`/roles/${id}/menus`).then((res) => res.data);
  },
  create(body: { code: string; name: string; description?: string; sort?: number }): Promise<RoleItem> {
    return http.post<RoleItem>('/roles', body).then((res) => res.data);
  },
  update(id: string, body: { name: string; description?: string; sort?: number }): Promise<RoleItem> {
    return http.put<RoleItem>(`/roles/${id}`, body).then((res) => res.data);
  },
  assignMenus(id: string, menuIds: string[]): Promise<void> {
    return http.put(`/roles/${id}/menus`, { menuIds }).then(() => undefined);
  },
  remove(id: string): Promise<void> {
    return http.delete(`/roles/${id}`).then(() => undefined);
  }
};

// ---- 菜单管理 ----

export interface MenuBody {
  parentId?: string | null;
  code?: string;
  name: string;
  type: 'directory' | 'menu' | 'button';
  path?: string | null;
  component?: string | null;
  icon?: string | null;
  permCode?: string | null;
  sort?: number;
  visible?: boolean;
  /** 所属工作区：app / mall；缺省 app。 */
  workspace?: string;
}

export const menuApi = {
  tree(): Promise<MenuNode[]> {
    return http.get<MenuNode[]>('/menus').then((res) => res.data);
  },
  create(body: MenuBody): Promise<{ id: string }> {
    return http.post<{ id: string }>('/menus', body).then((res) => res.data);
  },
  update(id: string, body: MenuBody): Promise<void> {
    return http.put(`/menus/${id}`, body).then(() => undefined);
  },
  remove(id: string): Promise<void> {
    return http.delete(`/menus/${id}`).then(() => undefined);
  }
};

// ---- 仪表盘统计 ----

export interface DashboardStats {
  productModelCount: number;
  deviceCount: number;
  onlineDeviceCount: number;
  todayNewDeviceCount: number;
  userCount: number;
  todayNewUserCount: number;
  todayFeedbackCount: number;
}

export const dashboardApi = {
  stats(): Promise<DashboardStats> {
    return http.get<DashboardStats>('/dashboard/stats').then((res) => res.data);
  }
};

// ---- 用户操作记录 / 审计日志 ----

export interface AuditLog {
  id: number;
  actorType: string;
  actorId: string | null;
  actorName: string | null;
  action: string;
  resourceType: string | null;
  resourceId: string | null;
  requestMethod: string | null;
  requestPath: string | null;
  responseStatus: number | null;
  durationMs: number | null;
  ip: string | null;
  /** 网络源端口（公安备案日志留存要求项，C 端记录才有） */
  sourcePort: number | null;
  /** 访问目标地址 host:port（同上） */
  targetAddress: string | null;
  userAgent: string | null;
  errorMessage: string | null;
  createdAt: string;
}

export interface AuditQuery {
  actorType?: string;
  action?: string;
  resourceType?: string;
  q?: string;
  from?: string;
  to?: string;
  page?: number;
  size?: number;
}

export const auditApi = {
  list(params: AuditQuery = {}): Promise<PageResult<AuditLog>> {
    return http.get<PageResult<AuditLog>>('/audit', { params }).then((res) => res.data);
  }
};

// ---- 用户中心（C 端用户档案 + 名下设备） ----

export interface UserSummary {
  id: string;
  phone: string | null;
  email: string | null;
  name: string | null;
  avatar: string | null;
  createdAt: string;
  deletedAt: string | null;
  preferredLocale: string | null;
  /** 已提交注销申请（含 7 天冷静期内，仍可恢复）。 */
  deleted: boolean;
  /** 冷静期到期时间；deleted && !anonymized 时用于算剩余天数。 */
  scheduledHardDeleteAt: string | null;
  /** 已完成硬删除（原地匿名化），不可恢复。 */
  anonymized: boolean;
}

export interface UserDevice {
  deviceId: string;
  name: string;
  type: string;
  productId: string | null;
  productName: string | null;
  connectionState: string;
  familyId: string;
  familyName: string;
  familyRole: string;
  createdAt: string;
}

export interface RewardSummary {
  balance: number;
  currentStreak: number;
  lastCheckInDate: string | null;
}

export interface UserDetail {
  user: UserSummary;
  reward: RewardSummary;
  devices: UserDevice[];
}

export interface CheckIn {
  id: string;
  checkInDate: string;
  basePoints: number;
  bonusPoints: number;
  streakDay: number;
  createdAt: string;
}

/** 一条积分流水（账本）：签到获得 / 兑换消费 / 后续运营调整。 */
export interface PointTransaction {
  id: string;
  /** 变动类型：CHECK_IN=签到、REDEEM=兑换，后续可扩 ADJUST 等。 */
  kind: string;
  /** 积分变动量：正=获得、负=消费。 */
  delta: number;
  /** 本次变动后的余额快照。 */
  balanceAfter: number;
  /** 展示标题快照（如「每日签到」「兑换：数字分身生成×1」）。 */
  title: string;
  createdAt: string;
}

export interface UserQuery {
  q?: string;
  includeDeleted?: boolean;
  page?: number;
  size?: number;
}

/** 用户数字分身额度概览（累计授予 / 已用 / 剩余）。 */
export interface MascotQuotaSummary {
  granted: number;
  used: number;
  remaining: number;
}

/** 违规昵称重置结果。 */
export interface NameResetResult {
  /** 被覆盖掉的原昵称；用户从未设置过昵称时为空串。 */
  oldName: string;
  /** 重置后写入的占位昵称，形如「用户_7f3a」。 */
  newName: string;
  /** 系统通知是否已成功创建；false=昵称已重置但通知没发出去。 */
  notified: boolean;
}

/** 昵称重置的「对外原因分类」选项：决定用户收到的通知文案。 */
export interface NameResetReasonOption {
  /** 落库值，提交时回传 publicReasonCode。 */
  code: string;
  /** 下拉里显示的分类名。 */
  label: string;
  /** 该分类下用户实际会收到的通知正文（占位名用样例值）。 */
  previewBody: string;
}

/** 一条数字分身额度流水（账本）。 */
export interface MascotQuotaTransaction {
  id: string;
  /** 类型：GRANT_DEVICE_BIND=设备首绑授予、CONSUME_GENERATE=生成消耗、REFUND_GENERATE=生成失败退还、GRANT_RECHARGE=充值发放、GRANT_REDEMPTION=积分兑换发放、GRANT_ADMIN_ADJUST=后台人工发放。 */
  type: string;
  /** 额度变动量：正=获得、负=消耗。 */
  delta: number;
  /** 本次变动后的剩余额度快照。 */
  balanceAfter: number;
  /** 来源引用：设备 SN / 充值订单号 / 兑换记录号等，可空。 */
  sourceRef: string | null;
  /** 人工发放的操作管理员用户名；系统自动流水为空。 */
  operatorName: string | null;
  /** 人工发放的原因说明；系统自动流水为空。 */
  reason: string | null;
  createdAt: string;
}

export const userApi = {
  list(params: UserQuery = {}): Promise<PageResult<UserSummary>> {
    return http.get<PageResult<UserSummary>>('/users', { params }).then((res) => res.data);
  },
  get(id: string): Promise<UserDetail> {
    return http.get<UserDetail>(`/users/${id}`).then((res) => res.data);
  },
  checkIns(id: string, params: { page?: number; size?: number } = {}): Promise<PageResult<CheckIn>> {
    return http.get<PageResult<CheckIn>>(`/users/${id}/check-ins`, { params }).then((res) => res.data);
  },
  pointTransactions(
    id: string,
    params: { page?: number; size?: number } = {}
  ): Promise<PageResult<PointTransaction>> {
    return http
      .get<PageResult<PointTransaction>>(`/users/${id}/point-transactions`, { params })
      .then((res) => res.data);
  },
  mascotQuota(id: string): Promise<MascotQuotaSummary> {
    return http.get<MascotQuotaSummary>(`/users/${id}/mascot-quota`).then((res) => res.data);
  },
  mascotQuotaTransactions(
    id: string,
    params: { page?: number; size?: number } = {}
  ): Promise<PageResult<MascotQuotaTransaction>> {
    return http
      .get<PageResult<MascotQuotaTransaction>>(`/users/${id}/mascot-quota-transactions`, { params })
      .then((res) => res.data);
  },
  /** 人工给用户发放数字分身生成次数；返回发放后的最新额度概览。需 user:quota:grant 权限。 */
  grantMascotQuota(
    id: string,
    payload: { amount: number; reason: string }
  ): Promise<MascotQuotaSummary> {
    return http
      .post<MascotQuotaSummary>(`/users/${id}/mascot-quota/grant`, payload)
      .then((res) => res.data);
  },
  /**
   * 把违规昵称重置为固定占位名，并给该用户发一条系统通知。需 user:name:reset 权限。
   * 返回里的 notified=false 表示昵称已重置但通知没发出去。
   */
  resetName(
    id: string,
    payload: { reason: string; publicReasonCode: string }
  ): Promise<NameResetResult> {
    return http.post<NameResetResult>(`/users/${id}/name/reset`, payload).then((res) => res.data);
  },
  /**
   * 对外原因分类选项（下拉 + 用户侧文案预览）。由后端下发而非前端写死：
   * previewBody 就是用户实际收到的通知正文，两边各写一份迟早对不上。
   */
  nameResetReasons(): Promise<NameResetReasonOption[]> {
    return http.get<NameResetReasonOption[]>('/users/name-reset-reasons').then((res) => res.data);
  }
};
